import { yupResolver } from "@hookform/resolvers/yup"
import { LoadingButton } from "@mui/lab"
import { Box, Divider, Link, Stack, Typography } from "@mui/material"
import { convertToLowercaseEmail } from "lib/case-helper"
import { RouterLink } from "lib/routes/router-link"
import useAuth from "minimals-template/components/@hooks/useAuth"
import { FormProvider, RHFTextField } from "minimals-template/components/hook-form"

import AuthTemplate from "minimals-template/pages/auth/AuthTemplate"
import { useForm } from "react-hook-form"

import { PATH_AUTH } from "routes/paths"

import * as yup from "yup"
import PrivacyAndTerms from "./PrivacyAndTerms"

import logo from "routes/@assets/PRIMARY LOGO _ LOCKUP _ REVERSED@3x.png"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useNavigate, useSearchParams } from "react-router-dom"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { useEffect, useRef, useState } from "react"
import { checkSSO } from "routes/settings/plugins/sso/controller/check-sso"
import { SSOLoginButton, triggerSSOLogin } from "./sso/sso-login-button"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { getCurrentAuthRedirectPath } from "minimals-template/components/contexts/auth-redirect"

const schema = yup.object().shape({
    email: yup.string().required().email(),
})

export default function PasswordlessLogin() {
    const { login } = useAuth()
    const [searchParams] = useSearchParams()
    const [requiredClient] = useSearchParam("requiredClient")
    const [ssoInfo, setSsoInfo] = useState(null)
    const [checkingSSO, setCheckingSSO] = useState(false)
    const ssoCheckRef = useRef(0)

    let email = searchParams.get("email")
    email = email ? decodeURIComponent(email) : ""

    const navigate = useNavigate()
    const defaultValues = {
        email: email || "",
    }

    useEffect(handleRedirect, [navigate])

    const methods = useForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        resolver: yupResolver(schema),
        defaultValues,
    })

    const { handleSubmit, watch } = methods
    const watchedEmail = watch("email")

    useEffect(checkSSOForEmail, [watchedEmail])

    return (
        <AuthTemplate
            pageTitle={"Login"}
            pageImage={logo}
            headerComponent={
                <Typography variant="body2" align="center" sx={{ mt: { md: -2 } }}>
                    Don’t have an account?{" "}
                    <Link
                        variant="subtitle2"
                        component={RouterLink}
                        to={PATH_AUTH.register}
                        data-cy="registration-link"
                    >
                        Get started
                    </Link>
                </Typography>
            }
        >
            <Stack>
                <Typography component="h1" variant="h4">
                    Sign in to SFG20
                </Typography>
                <If value={ssoInfo?.enforceSSO}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Your organisation requires single sign-on.
                    </Typography>
                    <Else>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Please enter your email below and we will send you a passcode to sign in.
                        </Typography>
                    </Else>
                </If>
                <Box sx={{ mt: 2 }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={2}>
                            <RHFTextField
                                transformOut={convertToLowercaseEmail}
                                name="email"
                                label="* Email address"
                                defaultValue={defaultValues.email}
                                data-cy="login-email-input"
                            />

                            <SSOLoginButton ssoInfo={ssoInfo} checkingSSO={checkingSSO} email={watchedEmail} />

                            <If value={ssoInfo && !ssoInfo.enforceSSO}>
                                <Divider>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        OR
                                    </Typography>
                                </Divider>
                            </If>
                            <If value={!ssoInfo?.enforceSSO}>
                                <LoadingButton
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant={ssoInfo ? "outlined" : "contained"}
                                    data-cy="send-link-button"
                                >
                                    Send Link
                                </LoadingButton>
                            </If>
                        </Stack>
                    </FormProvider>
                </Box>
                <PrivacyAndTerms />
            </Stack>
        </AuthTemplate>
    )

    function handleRedirect() {
        const registrationRedirectRoute = sessionStorage.getItem("registration-redirect")
        if (registrationRedirectRoute && window.location.pathname === PATH_AUTH.login) {
            sessionStorage.removeItem("registration-redirect")
            navigate(registrationRedirectRoute)
        } else {
            sessionStorage.setItem("registration-redirect", getCurrentAuthRedirectPath())
        }
    }

    function checkSSOForEmail() {
        if (!watchedEmail || !watchedEmail.includes("@")) {
            setSsoInfo(null)
            return undefined
        }
        const requestId = ++ssoCheckRef.current
        const timeoutId = setTimeout(performCheck, 500)
        return () => clearTimeout(timeoutId)

        async function performCheck() {
            setCheckingSSO(true)
            try {
                const result = await checkSSO(watchedEmail)
                if (ssoCheckRef.current !== requestId) return
                setSsoInfo(result.ssoAvailable ? result : null)
            } catch (e) {
                if (ssoCheckRef.current !== requestId) return
                setSsoInfo(null)
            } finally {
                if (ssoCheckRef.current === requestId) setCheckingSSO(false)
            }
        }
    }

    async function onSubmit(data) {
        let { email } = data
        if (ssoInfo?.enforceSSO) {
            triggerSSOLogin(ssoInfo, email)
            return
        }
        try {
            await login(email, requiredClient, window.location.href)
        } catch (error) {
            if (error?.status === 401) {
                navigate(
                    `/register?email=${encodeURIComponent(
                        email
                    )}&requiredClient=${requiredClient}&${searchParams.toString()}`
                )
                infoSnackbar("Welcome! We couldn’t find your email address, do you need to register?")
            } else if (error?.status === 403 && error?.body?.ssoEnforced) {
                infoSnackbar("Your organisation requires SSO. Redirecting...")
                triggerSSOLogin(ssoInfo || { configId: "" }, email)
            } else {
                errorSnackbar(error?.message || "We were unable to send you a login link.")
            }
        }
    }
}
